import React, { useCallback, useContext, useEffect, useState } from 'react';

import { t } from 'i18next';

import education from '../../../../assets/icons/v3/donations/education.svg?url';
import food from '../../../../assets/icons/v3/donations/food.svg?url';
import otherCauses from '../../../../assets/icons/v3/donations/other_causes.svg?url';
import trees from '../../../../assets/icons/v3/donations/trees.svg?url';
import water from '../../../../assets/icons/v3/donations/water.svg?url';
import BubbleChartCard from '../../../../shared/components/v3/Cards/BubbleChartCard/BubbleChartCard';
import EmptyCard from '../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import RankingCard from '../../../../shared/components/v3/Cards/RankingCard/RankingCard';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { MetricsContext } from '../../../../shared/context/metrics/metricsContext';
import { mapToRankingCardData } from '../../../../shared/handlers/dataMapper';
import { IRankingCardData } from '../../../../shared/interfaces/cards';
import { IMetricsDonationsChartData, IMetricsDonationsSummary } from '../../../../shared/interfaces/metrics';
import MetricsByCompany from '../../../../shared/services/api/metrics/metricsByCompany/metrics';
import { TRANSLATIONS_METRICS_NE } from '../../../../shared/translations/metrics';

const DonationsMetrics = () => {
  const [rankingData, setRankingData] = useState<IRankingCardData>({});
  const [summaryData, setSummaryData] = useState<IMetricsDonationsSummary>();
  const [chartData, setChartData] = useState<IMetricsDonationsChartData>();
  const [loadingRanking, setLoadingRanking] = useState<boolean>(false);
  const [loadingBubbleChart, setLoadingBubbleChart] = useState<boolean>(false);
  const [rankingDonationsError, setRankingDonationsError] = useState<boolean>(false);
  const [donationsSummaryError, setDonationsSummaryError] = useState<boolean>(false);

  const { company } = useContext(CompanyContext);
  const { periodSelection } = useContext(MetricsContext);

  const donationsType: Record<string, { icon: string; title: string }> = {
    water: { icon: water, title: t(TRANSLATIONS_METRICS_NE.donations.rankingCard.water) },
    food: { icon: food, title: t(TRANSLATIONS_METRICS_NE.donations.rankingCard.food) },
    trees: { icon: trees, title: t(TRANSLATIONS_METRICS_NE.donations.rankingCard.trees) },
    education: { icon: education, title: t(TRANSLATIONS_METRICS_NE.donations.rankingCard.education) },
    other: { icon: otherCauses, title: t(TRANSLATIONS_METRICS_NE.donations.rankingCard.other) }
  };

  const getDonationRankingData = useCallback(async () => {
    setLoadingRanking(true);
    setRankingDonationsError(false);
    try {
      const rankingResponse = await MetricsByCompany.getDonationsRankingMetrics(
        company.id,
        periodSelection.period,
        periodSelection.month
      );
      const mappedData = mapToRankingCardData(rankingResponse);
      const dataWithIcons = Object.keys(mappedData).reduce<IRankingCardData>(
        (acc, key) => ({
          ...acc,
          [key]: {
            ...mappedData[key],
            data: mappedData[key].data.map((item) => ({
              ...item,
              icon: donationsType[item.id]?.icon || donationsType.other.icon,
              title: donationsType[item.id]?.title || donationsType.other.title
            }))
          }
        }),
        {}
      );
      setRankingData(dataWithIcons);
    } catch {
      setRankingDonationsError(true);
    } finally {
      setLoadingRanking(false);
    }
  }, [company, periodSelection]);

  const getDonationsChartData = useCallback(async () => {
    setLoadingBubbleChart(true);
    setDonationsSummaryError(false);
    try {
      const [summaryResponse, donationsByCausesResponse] = await Promise.all([
        MetricsByCompany.getDonationsSummaryMetrics(company.id, periodSelection.period, periodSelection.month),
        MetricsByCompany.getDonationsByCausesMetrics(company.id, periodSelection.period, periodSelection.month)
      ]);
      setSummaryData(summaryResponse);
      setChartData(donationsByCausesResponse);
    } catch {
      setDonationsSummaryError(true);
    } finally {
      setLoadingBubbleChart(false);
    }
  }, [company, periodSelection]);

  useEffect(() => {
    getDonationRankingData();
    getDonationsChartData();
  }, [periodSelection, company, getDonationRankingData, getDonationsChartData]);

  return (
    <div className="metrics-content-section-donations">
      <div className="first-component">
        <BubbleChartCard
          title={t(TRANSLATIONS_METRICS_NE.donations.chartCard.title)}
          colorScheme="donations"
          unitsName={t(TRANSLATIONS_METRICS_NE.donations.chartCard.bubbleLabel)}
          cardData={
            summaryData
              ? [
                  {
                    value: `${summaryData.totalDonations}`,
                    label: t(TRANSLATIONS_METRICS_NE.donations.chartCard.donations, {
                      count: Number(summaryData.totalDonations)
                    })
                  },
                  {
                    value: `${summaryData.foundationsAmount}`,
                    label: t(TRANSLATIONS_METRICS_NE.donations.chartCard.totalCauses, {
                      count: Number(summaryData.foundationsAmount)
                    })
                  }
                ]
              : []
          }
          chartData={chartData?.causes}
          isPercentage={true}
          loading={loadingBubbleChart}
          error={donationsSummaryError}
          action={getDonationsChartData}
        />
      </div>
      <div className="second-component">
        <RankingCard
          title={t(TRANSLATIONS_METRICS_NE.rankingCard.donations.title)}
          data={rankingData}
          valueText={t(TRANSLATIONS_METRICS_NE.rankingCard.donations.valueText)}
          error={rankingDonationsError}
          loading={loadingRanking}
          hasTabBar={false}
          suffixIconType="circle-indicator"
          action={getDonationRankingData}
          emptyRankingCard={
            <EmptyCard
              primaryText={t(TRANSLATIONS_METRICS_NE.rankingCard.emptyState.emptyStateTitle)}
              secondaryText={t(TRANSLATIONS_METRICS_NE.rankingCard.emptyState.noDataDescription)}
            />
          }
        />
      </div>
    </div>
  );
};

export default DonationsMetrics;
